import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import * as filtersActions from 'syft-acp-core/store/filters/actions'
import { Store } from 'syft-acp-core/store'

import { FilterOfferTypesProps as Props } from './FilterOfferTypes.types'
import { filterSelect } from '../FilterSelect'
import FilterGroup from 'syft-acp-uikit/components/AcpTable/filterComponents/FilterGroup'
import IconInfo from 'syft-acp-core/components/IconInfo'
import InformationTip from 'syft-acp-util/components/InformationTip'
import { GLOSS_ALL_FLEXERS, LABEL_ALL_FLEXERS } from 'syft-acp-core/entities/ShiftList/helpers/constants'

type OfferTypeFilter = {
  title: string
  key: string
  // Shown on hover of an info icon beside the group label. The icon is decorative, so the
  // same text also reaches assistive tech via aria-describedby on the select.
  gloss?: string
  values: { label: string; id: string; title?: string }[]
}

export const storeConnector = connect(
  (state: Store) => ({
    query: state.routing.locationBeforeTransitions.query as Record<string, string | string[]>,
  }),
  dispatch => ({
    actions: {
      ...bindActionCreators(filtersActions, dispatch),
    },
  }),
)

export const filters: OfferTypeFilter[] = [
  {
    title: 'Flex offer types',
    key: 'offer_to_flex',
    gloss: GLOSS_ALL_FLEXERS,
    values: [
      { label: LABEL_ALL_FLEXERS, id: 'all', title: GLOSS_ALL_FLEXERS },
      { label: 'Indeed Flex pool', id: 'network' },
      { label: 'Individual Flexers', id: 'individual' },
    ],
  },
  {
    title: 'Internal offer types',
    key: 'offer_to_internal',
    values: [
      { label: 'All staff', id: 'all' },
      { label: 'Primary venue staff', id: 'network' },
      { label: 'Individual staff', id: 'individual' },
    ],
  },
  {
    title: 'Agency offer types',
    key: 'offer_to_agency',
    values: [
      { label: 'Agency', id: 'agency' },
      { label: 'Not agency', id: 'not_agency' },
    ],
  },
]

// Data for this component are called similary to roles during initial loading here loadInitData
const FilterOfferTypes = ({
  defaultOption = 'Any',
  className = '',
  actions,
  allowAny = false,
  type,
  small = false,
  tiny = false,
  query,
  onChange,
}: Props) => {
  const onChangeFilterHandler = (key: string) => (val: string | string[]) => {
    !!onChange && onChange(key, val?.toString())
    const availableFilters = filters.map(filter => filter.key).filter(filter => filter !== key)
    availableFilters.forEach(filterKey => actions.setFilter(filterKey, null))
    actions.setFilter(key, val)
  }

  const FilterSelectElements = filters.map(filter => {
    const value = query[filter.key]
    const FilterSelectElement = filterSelect(filter.values, defaultOption || 'Any', type, false, false)
    const glossID = `${filter.key}Gloss`
    const glossTextID = `${filter.key}GlossText`

    return (
      // FilterGroup's label wraps the select, so the screen-reader text and the tooltip body
      // sit outside it. Nested there, they would be absorbed into the select's accessible name.
      <React.Fragment key={filter.key}>
        <FilterGroup
          title={
            filter.gloss ? (
              <>
                {filter.title}
                <IconInfo tooltipID={glossID} testID={glossID} />
              </>
            ) : (
              filter.title
            )
          }
        >
          <FilterSelectElement
            name={filter.key}
            className={`${className}`}
            onChange={onChangeFilterHandler(filter.key)}
            value={value}
            small={small}
            tiny={tiny}
            allowAny={allowAny}
            ariaDescribedBy={filter.gloss ? glossTextID : undefined}
          />
        </FilterGroup>
        {filter.gloss && (
          <>
            <span id={glossTextID} className="sr-only">
              {filter.gloss}
            </span>
            <InformationTip Id={glossID} message={filter.gloss} className="glossTip" />
          </>
        )}
      </React.Fragment>
    )
  })

  return <>{FilterSelectElements}</>
}

export default storeConnector(FilterOfferTypes)
